import { useCallback, useEffect, useState } from 'react'
import { Tooltip, Button } from '@overdose/components'
import { IconChevronDown } from '@tabler/icons-react'
import classNames from 'classnames'
import { BaseMoney } from 'shared-types/src/commercetools'
import { Checkbox } from '~/components/Checkbox'
import { Radio } from '~/components/Radio'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'

import { PriceRange } from './PriceRange'
import styles from './ProductListFilters.module.css'
import { FilterItemProps } from './ProductListFilters.types'

const FilterItem = ({
  text,
  suffix,
  selectionType,
  options,
  selected,
  onSelect,
  uid,
  buttonClassName = '',
  suffixClassName = 'hidden sm:inline',
}: FilterItemProps) => {
  const [open, setOpen] = useState(false)
  const offsetY = 8
  const centsToDollar = 100
  const [hasFilter, setHasFilter] = useState<boolean>(false)

  const getLabelText = () => {
    if (selectionType !== 'single') {
      return text
    }

    return (
      options.find((op) => {
        return selected?.[uid]?.includes(op.uid)
      })?.text || ''
    )
  }

  const getPrice = useCallback(
    (uid) => {
      const value = options.find((option) => {
        return option.uid === uid
      })?.value as BaseMoney

      if (selected && selected[uid]?.[0]) {
        return {
          ...value,
          centAmount: Number(selected[uid]?.[0]) * centsToDollar,
        }
      }
      return value
    },
    [selected, options]
  )

  const renderLabel = useCallback((text: string, count: number) => {
    return (
      <div className='flex mr-4 gap-4 items-center'>
        <div className='flex-1'>
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyRegular}>
            {text}
          </Typography>
        </div>
        <div>
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyRegular}
            className='w-[25px]'>
            {count}
          </Typography>
        </div>
      </div>
    )
  }, [])

  useEffect(() => {
    if (selectionType === 'pricerange') {
      const minPrice = options.find((option) => {
        return option.uid === 'minPrice'
      })?.value as BaseMoney

      const maxPrice = options.find((option) => {
        return option.uid === 'maxPrice'
      })?.value as BaseMoney
      setHasFilter(
        minPrice.centAmount !== getPrice('minPrice').centAmount ||
          maxPrice.centAmount !== getPrice('maxPrice').centAmount
      )
    } else {
      setHasFilter(!!(selected?.[uid] && selected?.[uid][0]))
    }
  }, [selected, options, selectionType, getPrice, uid])

  return (
    <Tooltip
      theme={{ root: styles.popInner }}
      trigger='click'
      triggerProps={{
        onOpenDelay: 80,
        clickOutsideToClose: true,
        onOpen: () => {
          return setOpen(true)
        },
        onClose: () => {
          return setOpen(false)
        },
      }}
      position='bottom-start'
      offset={[0, offsetY]}
      content={
        selectionType === 'pricerange' ? (
          <PriceRange
            minPrice={getPrice('minPrice')}
            maxPrice={getPrice('maxPrice')}
            onChange={onSelect}
          />
        ) : (
          options.map((option, index) => {
            let isChecked
            if (selectionType === 'single') {
              isChecked = selected?.[uid]?.includes(option.value as string)
            } else {
              isChecked = selected?.[uid]
                ? selected?.[uid]?.includes(option.uid)
                : false
            }

            return (
              <button
                key={index}
                type='button'
                className={classNames(
                  styles.option,
                  'flex items-center gap-x-4 py-2 px-1 w-full text-left cursor-pointer filter-option'
                )}>
                {selectionType === 'single' ? (
                  <Radio
                    label={renderLabel(option.text, option.count)}
                    theme={{
                      root: 'w-full',
                      content: 'order-1 flex-1 max-w-none',
                      radio: 'order-2',
                    }}
                    name={option.uid || 'recommended'}
                    defaultChecked={false}
                    checked={isChecked}
                    onChange={() => {
                      onSelect(uid, option.value as string, true)
                    }}
                  />
                ) : (
                  <Checkbox
                    label={renderLabel(option.text, option.count)}
                    name={option.uid}
                    theme={{
                      root: 'w-full',
                      content: 'order-1 flex-1 max-w-none',
                      box: 'order-2',
                    }}
                    size='md'
                    defaultChecked={false}
                    checked={isChecked}
                    onChange={(_isChecked) => {
                      onSelect(uid, option.uid, _isChecked)
                    }}
                  />
                )}
              </button>
            )
          })
        )
      }>
      <Button
        status={hasFilter ? 'primary' : 'secondary'}
        shape='rounded'
        theme={{
          root: classNames(styles.filterBtn, buttonClassName, {
            'text-secondary-heading': hasFilter,
            'text-primary-heading': !hasFilter,
            [styles.selectedBtn]: open,
          }),
        }}
        icon={
          <IconChevronDown className={classNames({ 'rotate-180': open })} />
        }
        iconAlignment='right'>
        <Typography
          tag={TypographyTag.span}
          variant={TypographyVariant.BodySmallBold}>
          {suffix && (
            <span
              className={classNames(
                'font-normal',
                '[&+span]:ml-2 [&+span]:sm:ml-0',
                styles.suffix,
                suffixClassName,
                {
                  [styles.active]: hasFilter,
                }
              )}>
              {suffix}
            </span>
          )}{' '}
          <span>{getLabelText()}</span>
        </Typography>
      </Button>
    </Tooltip>
  )
}

export default FilterItem
